import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Constants } from 'src/app/app.constants';
import {
  IntegrationConfig,
  IntegrationConfiguration,
  PublicApiKey,
  PublicApiKeyCreatedResponse,
  PublicApiUsageReport,
} from 'src/app/models/integrationConfig';
import { MerchantService } from 'src/app/services';
import { ConfigurationDialogComponent } from 'src/app/shared/components/configuration-dialog/configuration-dialog.component';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';

interface ApiScopeOption {
  value: string;
  label: string;
  description: string;
  readOnly?: boolean;
}

@Component({
  selector: 'app-api-integration-dialog',
  templateUrl: './api-integration-dialog.component.html',
  styleUrls: [
    './api-integration-dialog.component.css',
    './api-integration-dialog-usage.component.css',
  ],
})
export class ApiIntegrationDialogComponent implements OnInit {
  readonly docsUrl = 'https://developer.remyrewards.co.uk';
  readonly scopeOptions: ApiScopeOption[] = [
    { value: 'profile:read', label: 'Profile', description: 'Read the authenticated merchant profile.', readOnly: true },
    { value: 'usage:read', label: 'Usage', description: 'Read API usage and rate-limit information.', readOnly: true },
    { value: 'orders:read', label: 'Orders — read', description: 'List and retrieve orders.', readOnly: true },
    { value: 'orders:write', label: 'Orders — write', description: 'Create or change order data.' },
    { value: 'customers:read', label: 'Customers', description: 'Read customers, loyalty cards, and issued rewards.', readOnly: true },
    { value: 'customers:write', label: 'Customer loyalty — write', description: 'Give stamps, points, and rewards or adjust customer points.' },
    { value: 'rewards:read', label: 'Reward templates', description: 'List and retrieve merchant reward templates.', readOnly: true },
    { value: 'rewards:write', label: 'Rewards — write', description: 'Create or change reward data.' },
    { value: 'rewards:redeem', label: 'Rewards — redeem', description: 'Redeem rewards for customers.' },
    { value: 'cards:read', label: 'Card templates', description: 'List and retrieve merchant loyalty-card templates.', readOnly: true },
    { value: 'vouchers:read', label: 'Vouchers — read', description: 'Look up and retrieve issued vouchers.', readOnly: true },
    { value: 'vouchers:issue', label: 'Vouchers — issue', description: 'Issue and send new vouchers.' },
    { value: 'vouchers:redeem', label: 'Vouchers — redeem', description: 'Fully or partially redeem vouchers.' },
    { value: 'vouchers:manage', label: 'Vouchers — manage', description: 'Cancel and adjust issued vouchers.' },
  ];

  keys: PublicApiKey[] = [];
  loading = false;
  saving = false;
  showForm = false;
  editingKey?: PublicApiKey;
  name = '';
  expiresAt = '';
  selectedScopes = new Set<string>();
  revealedSecret?: string;
  revealedKeyName?: string;
  usage?: PublicApiUsageReport;
  usageDays: 7 | 30 | 90 = 30;
  usageLoading = false;
  apiConfig?: IntegrationConfig;
  awardStampsApi = false;
  inviteUsersApi = false;
  rewardAutomationApi = false;

  constructor(
    public dialogRef: MatDialogRef<ApiIntegrationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private merchantService: MerchantService,
    private constants: Constants,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadKeys();
    this.loadOrderSettings();
  }

  loadKeys(): void {
    this.loading = true;
    this.merchantService
      .getPublicApiKeys()
      .subscribe({
        next: (keys) => (this.keys = keys),
        error: (error: HttpErrorResponse) => this.showError(error),
      })
      .add(() => (this.loading = false));
  }

  tabChanged(index: number): void {
    if (index === 3 && !this.usage) this.loadUsage();
  }

  loadOrderSettings(): void {
    this.loading = true;
    this.merchantService
      .getIntegration('api')
      .subscribe({
        next: (config?: IntegrationConfig) => {
          this.apiConfig = config;
          this.awardStampsApi = config?.apiSettings?.awardStamps ?? false;
          this.inviteUsersApi = config?.inviteUser?.active ?? false;
          this.rewardAutomationApi =
            config?.apiSettings?.rewardAutomation?.enabled ?? false;
        },
        error: (error: HttpErrorResponse) => this.showError(error),
      })
      .add(() => (this.loading = false));
  }

  saveOrderSettings(): void {
    if (this.awardStampsApi && !this.apiConfig?.configurations?.length) {
      return this.constants.snack(
        'Add an order configuration before enabling API order awards',
      );
    }

    this.saving = true;
    this.merchantService
      .updatePublicApiOrderSettings({
        awardStamps: this.awardStampsApi,
        inviteUsers: this.inviteUsersApi,
        rewardAutomation: { enabled: this.rewardAutomationApi },
      })
      .subscribe({
        next: (config) => {
          this.apiConfig = config;
          this.constants.snack('API order settings saved');
        },
        error: (error: HttpErrorResponse) => this.showError(error),
      })
      .add(() => (this.saving = false));
  }

  addConfiguration(): void {
    const ref = this.dialog.open(ConfigurationDialogComponent, {
      width: '500px',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
      data: { orderAvailable: true, type: 'API' },
      scrollStrategy: new NoopScrollStrategy(),
    });
    ref.afterClosed().subscribe((configuration?: IntegrationConfiguration) => {
      if (configuration) this.addNewConfiguration(configuration);
    });
  }

  addNewConfiguration(configuration: IntegrationConfiguration): void {
    this.saving = true;
    this.merchantService
      .addIntegrationConfiguration(configuration, 'api')
      .subscribe({
        next: (config: IntegrationConfig) => (this.apiConfig = config),
        error: (error: HttpErrorResponse) => this.showError(error),
      })
      .add(() => (this.saving = false));
  }

  editConfiguration(configuration: IntegrationConfiguration): void {
    const ref = this.dialog.open(ConfigurationDialogComponent, {
      width: '500px',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
      data: { configuration, type: 'API' },
      scrollStrategy: new NoopScrollStrategy(),
    });
    ref.afterClosed().subscribe((updated?: IntegrationConfiguration) => {
      if (updated) this.updateConfiguration(updated);
    });
  }

  updateConfiguration(configuration: IntegrationConfiguration): void {
    this.saving = true;
    this.merchantService
      .updateIntegrationConfiguration(configuration, 'api')
      .subscribe({
        next: (config: IntegrationConfig) => (this.apiConfig = config),
        error: (error: HttpErrorResponse) => this.showError(error),
      })
      .add(() => (this.saving = false));
  }

  deleteConfiguration(id: string): void {
    this.confirm(
      'Remove configuration',
      'Remove this API order configuration?',
      'Remove',
      () => {
        this.saving = true;
        this.merchantService
          .deleteIntegrationConfiguration(id, 'api')
          .subscribe({
            next: (config: IntegrationConfig) => {
              this.apiConfig = config;
              if (!config.configurations?.length) this.awardStampsApi = false;
            },
            error: (error: HttpErrorResponse) => this.showError(error),
          })
          .add(() => (this.saving = false));
      },
    );
  }

  rowLabel(item: IntegrationConfiguration): string {
    const quantity = item.numberOfStamps ?? item.numberOfPoints;
    const unit = item.numberOfStamps
      ? item.numberOfStamps === 1
        ? 'stamp'
        : 'stamps'
      : item.numberOfPoints === 1
        ? 'point'
        : 'points';

    if (item.method === 'incremental' && !item.numberOfStamps) {
      const extras = [
        this.pointRoundingLabel(item.pointsRoundingMode),
        item.cap ? `up to ${item.cap} points` : undefined,
      ].filter(Boolean);
      return `Earn ${quantity} ${unit} for every ${item.value} spent${
        extras.length ? ` (${extras.join(', ')})` : ''
      }`;
    }
    return `Spend ${item.value} or more to get ${quantity} ${unit}`;
  }

  pointRoundingLabel(mode?: string): string {
    if (mode === 'up') return 'round up';
    if (mode === 'nearest') return 'round to nearest';
    if (mode === 'exact') return 'exact points';
    return 'round down';
  }

  changeUsagePeriod(days: number | string): void {
    this.usageDays = Number(days) as 7 | 30 | 90;
    this.loadUsage();
  }

  loadUsage(): void {
    this.usageLoading = true;
    this.merchantService
      .getPublicApiKeyUsage(this.usageDays)
      .subscribe({
        next: (usage) => (this.usage = usage),
        error: (error: HttpErrorResponse) => this.showError(error),
      })
      .add(() => (this.usageLoading = false));
  }

  exportUsage(): void {
    if (!this.usage) return;
    const rows: Array<Array<string | number>> = [[
      'Key name',
      'Key identifier',
      'Status',
      'Method',
      'Endpoint',
      'Requests',
      'Errors',
      'Error rate',
      'Average duration (ms)',
      'Last request',
    ]];
    this.usage.keys.forEach((key) => {
      const endpoints = key.endpoints.length ? key.endpoints : [undefined];
      endpoints.forEach((endpoint) => rows.push([
        key.name,
        `${key.keyPrefix}…${key.lastFour}`,
        key.status,
        endpoint?.method || '',
        endpoint?.route || '',
        endpoint?.requests ?? 0,
        endpoint?.errors ?? 0,
        endpoint?.requests ? `${((endpoint.errors / endpoint.requests) * 100).toFixed(2)}%` : '0%',
        endpoint?.averageDurationMs ?? 0,
        endpoint?.lastRequestAt ? new Date(endpoint.lastRequestAt).toISOString() : '',
      ]));
    });
    const csv = rows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `remy-api-usage-${this.usageDays}-days-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  startCreate(): void {
    this.editingKey = undefined;
    this.name = '';
    this.expiresAt = '';
    this.selectedScopes = new Set(
      this.scopeOptions.filter((scope) => scope.readOnly).map((scope) => scope.value),
    );
    this.showForm = true;
  }

  startEdit(key: PublicApiKey): void {
    this.editingKey = key;
    this.name = key.name;
    this.expiresAt = key.expiresAt
      ? new Date(key.expiresAt).toISOString().slice(0, 10)
      : '';
    this.selectedScopes = new Set(key.scopes);
    this.showForm = true;
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingKey = undefined;
  }

  toggleScope(scope: string, checked: boolean): void {
    checked ? this.selectedScopes.add(scope) : this.selectedScopes.delete(scope);
  }

  save(): void {
    const name = this.name.trim();
    if (!name) return this.constants.snack('Enter a name for this API key');
    if (!this.selectedScopes.size) return this.constants.snack('Select at least one scope');
    const wasEditing = Boolean(this.editingKey);
    const payload = {
      name,
      scopes: Array.from(this.selectedScopes),
      expiresAt: this.expiresAt
        ? `${this.expiresAt}T23:59:59.999Z`
        : wasEditing
          ? null
          : undefined,
    };

    this.saving = true;
    const request: any = this.editingKey
      ? this.merchantService.updatePublicApiKey(this.editingKey._id, payload)
      : this.merchantService.createPublicApiKey(payload);
    request
      .subscribe({
        next: (response: PublicApiKey | PublicApiKeyCreatedResponse) => {
          if ('secret' in response) this.revealSecret(response);
          this.cancelForm();
          this.loadKeys();
          this.constants.snack(wasEditing ? 'API key updated' : 'API key created');
        },
        error: (error: HttpErrorResponse) => this.showError(error),
      })
      .add(() => (this.saving = false));
  }

  rotate(key: PublicApiKey): void {
    this.confirm(
      'Rotate API key',
      'Rotate this API key? The existing key will stop working immediately.',
      'Rotate key',
      () => {
        this.saving = true;
        this.merchantService
          .rotatePublicApiKey(key._id)
          .subscribe({
            next: (response) => {
              this.revealSecret(response);
              this.loadKeys();
              this.constants.snack('API key rotated');
            },
            error: (error: HttpErrorResponse) => this.showError(error),
          })
          .add(() => (this.saving = false));
      },
    );
  }

  revoke(key: PublicApiKey): void {
    this.confirm(
      'Revoke API key',
      'Revoke this API key? Requests using it will fail immediately. This cannot be undone.',
      'Revoke key',
      () => {
        this.saving = true;
        this.merchantService
          .revokePublicApiKey(key._id)
          .subscribe({
            next: () => {
              this.loadKeys();
              this.constants.snack('API key revoked');
            },
            error: (error: HttpErrorResponse) => this.showError(error),
          })
          .add(() => (this.saving = false));
      },
    );
  }

  revealSecret(response: PublicApiKeyCreatedResponse): void {
    this.revealedSecret = response.secret;
    this.revealedKeyName = response.apiKey.name;
  }

  async copySecret(): Promise<void> {
    if (!this.revealedSecret) return;
    try {
      await navigator.clipboard.writeText(this.revealedSecret);
      this.constants.snack('API key copied');
    } catch {
      this.constants.snack('Copy failed. Select the key and copy it manually.');
    }
  }

  dismissSecret(): void {
    this.revealedSecret = undefined;
    this.revealedKeyName = undefined;
  }

  private confirm(title: string, body: string, action: string, callback: () => void): void {
    const ref = this.dialog.open(TwoOptionAlertComponent, {
      data: { title, body, buttonOne: 'Cancel', buttonTwo: action },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      disableClose: true,
    });
    ref.afterClosed().subscribe((option: number) => {
      if (option === 1) callback();
    });
  }

  private showError(error: HttpErrorResponse): void {
    this.constants.snack(error.error?.message || 'Unable to manage API keys');
  }
}
